import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateWorkspace } from '@/api/workspaces';
import { Workspace, UpdateWorkspaceInput } from '@/types/workspaces';

interface WorkspaceEditModalProps {
  open: boolean;
  workspace: Workspace | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const WorkspaceEditModal: React.FC<WorkspaceEditModalProps> = ({
  open,
  workspace,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation(['workspace', 'common']);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateWorkspaceInput) => {
      if (!workspace?.id) throw new Error('No workspace ID available');
      return updateWorkspace(workspace.id, data);
    },
    onSuccess: () => {
      message.success(t('workspace:success.update', 'Workspace updated successfully'));
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
      onSuccess();
    },
    onError: (error: Error) => {
      message.error(error.message || t('workspace:errors.updateFailed', 'Failed to update workspace'));
    },
  });

  // Initialize form when modal opens
  useEffect(() => {
    if (open && workspace) {
      form.setFieldsValue({
        name: workspace.name,
        slug: workspace.slug,
        language: workspace.language || 'en',
        currency: workspace.currency || 'USD',
        presidentName: workspace.presidentName || '',
        presidentEmail: workspace.presidentEmail || '',
        presidentPhone: workspace.presidentPhone || '',
        status: workspace.status || 'active',
      });
    }
  }, [open, workspace, form]);

  // Tour functionality removed

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await updateMutation.mutateAsync(values);
    } catch (error) {
      // Form validation failed or mutation failed
      console.error('Form submission failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('workspace:actions.edit', 'Edit Workspace')}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={updateMutation.isPending}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
      >
        <Form.Item
          name="name"
          label={t('workspace:form.name', 'Workspace Name')}
          rules={[
            { required: true, message: t('common:fieldRequired', 'This field is required') },
            { min: 2, message: t('workspace:errors.nameTooShort', 'Name must be at least 2 characters') },
            { max: 100, message: t('workspace:errors.nameTooLong', 'Name must be less than 100 characters') }
          ]}
        >
          <Input 
            id="workspace-name-input"
            placeholder={t('workspace:form.namePlaceholder', 'Enter workspace name')} 
          />
        </Form.Item>

        <Form.Item
          name="slug"
          label={t('workspace:form.slug', 'Workspace Slug')}
          rules={[
            { required: true, message: t('common:fieldRequired', 'This field is required') },
            { 
              pattern: /^[a-z0-9-]+$/, 
              message: t('workspace:errors.invalidSlug', 'Slug can only contain lowercase letters, numbers, and hyphens') 
            },
            { min: 2, message: t('workspace:errors.slugTooShort', 'Slug must be at least 2 characters') },
            { max: 50, message: t('workspace:errors.slugTooLong', 'Slug must be less than 50 characters') }
          ]}
        >
          <Input 
            id="workspace-slug-input"
            placeholder={t('workspace:form.slugPlaceholder', 'Enter workspace slug')} 
          />
        </Form.Item>

        <Form.Item
          name="language"
          label={t('workspace:form.language', 'Language')}
          rules={[{ required: true, message: t('common:fieldRequired', 'This field is required') }]}
        >
          <Select 
            id="workspace-language-select"
            placeholder={t('workspace:form.languagePlaceholder', 'Select language')}
          >
            <Select.Option value="en">{t('common:languages.en', 'English')}</Select.Option>
            <Select.Option value="es">{t('common:languages.es', 'Spanish')}</Select.Option>
            <Select.Option value="pt">{t('common:languages.pt', 'Portuguese')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="currency"
          label={t('workspace:form.currency', 'Currency')}
          rules={[{ required: true, message: t('common:fieldRequired', 'This field is required') }]}
        >
          <Select 
            id="workspace-currency-select"
            placeholder={t('workspace:form.currencyPlaceholder', 'Select currency')}
          >
            <Select.Option value="USD">{t('common:currencies.USD', 'US Dollar')}</Select.Option>
            <Select.Option value="EUR">{t('common:currencies.EUR', 'Euro')}</Select.Option>
            <Select.Option value="BRL">{t('common:currencies.BRL', 'Brazilian Real')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label={t('workspace:form.status', 'Status')}
          rules={[{ required: true, message: t('common:fieldRequired', 'This field is required') }]}
        >
          <Select placeholder={t('workspace:form.statusPlaceholder', 'Select status')}>
            <Select.Option value="active">{t('workspace:status.active', 'Active')}</Select.Option>
            <Select.Option value="inactive">{t('workspace:status.inactive', 'Inactive')}</Select.Option>
            <Select.Option value="pending">{t('workspace:status.pending', 'Pending')}</Select.Option>
            <Select.Option value="suspended">{t('workspace:status.suspended', 'Suspended')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="presidentName"
          label={t('workspace:form.presidentName', 'President Name')}
        >
          <Input placeholder={t('workspace:form.presidentNamePlaceholder', 'Enter president name')} />
        </Form.Item>

        <Form.Item
          name="presidentEmail"
          label={t('workspace:form.presidentEmail', 'President Email')}
          rules={[
            { type: 'email', message: t('common:invalidEmail', 'Please enter a valid email address') }
          ]}
        >
          <Input placeholder={t('workspace:form.presidentEmailPlaceholder', 'Enter president email')} />
        </Form.Item>

        <Form.Item
          name="presidentPhone"
          label={t('workspace:form.presidentPhone', 'President Phone')}
        >
          <Input placeholder={t('workspace:form.presidentPhonePlaceholder', 'Enter president phone')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WorkspaceEditModal;
